/**
 * Canonical ID definitions for CloudExportDialog
 * This file is dependency-free to avoid circular dependencies.
 * These arrays serve as the single source of truth for enum values.
 */

/**
 * Cloud provider IDs - canonical list of all supported cloud providers
 */
export const CLOUD_PROVIDER_IDS = [
  'google-drive',
  'one-drive',
  'dropbox',
  'box',
  'snowflake',
  'servicenow',
  'google-cloud-storage',
  'hubspot',
  'signnow',
  'zoho',
  'confluence',
  'quickbooks',
  'slack',
  'notion',
  'trello',
] as const;

/** Canonical Trello provider id (member of CLOUD_PROVIDER_IDS). */
export const TRELLO_PROVIDER_ID = 'trello' as (typeof CLOUD_PROVIDER_IDS)[number];

/** Trello folder/listing API object_type values (export destination must be a list). */
export const TRELLO_BOARD_OBJECT_TYPE = 'trello_board' as const;
export const TRELLO_LIST_OBJECT_TYPE = 'trello_list' as const;

/**
 * Export format IDs - canonical list of all supported export formats
 */
export const EXPORT_FORMAT_IDS = ['pdf', 'docx', 'xlsx', 'csv', 'txt', 'md', 'jpg'] as const;

/**
 * Provider category IDs - canonical list of all provider categories
 */
export const PROVIDER_CATEGORY_IDS = [
  'cloud-platforms',
  'cloud-storage',
  'data-management',
  'case-management',
  'signature-management',
  'documentation',
  'team-collaboration',
] as const;

/**
 * Zoho CRM module IDs - canonical list of all Zoho CRM modules
 */
export const ZOHO_CRM_MODULE_IDS = [
  'Contacts',
  'Leads',
  'Accounts',
  'Deals',
  'Potentials',
  'Campaigns',
  'Cases',
  'Solutions',
] as const;

/**
 * Snowflake write mode IDs - canonical list of write modes
 */
export const SNOWFLAKE_WRITE_MODE_IDS = ['append', 'overwrite', 'error_if_exists'] as const;

/**
 * Upload progress stage IDs - canonical list of upload stages
 */
export const UPLOAD_PROGRESS_STAGE_IDS = ['preparing', 'uploading', 'completed', 'error'] as const;

/**
 * Provider folder/channel label map
 * Maps provider IDs to their folder/channel label for UI display
 */
export const PROVIDER_FOLDER_LABELS: Record<(typeof CLOUD_PROVIDER_IDS)[number], string> = {
  'google-drive': 'Google Drive folder',
  'one-drive': 'OneDrive folder',
  dropbox: 'Dropbox folder',
  box: 'Box folder',
  slack: 'Slack channel',
  snowflake: 'folder',
  servicenow: 'folder',
  'google-cloud-storage': 'folder',
  hubspot: 'folder',
  signnow: 'folder',
  zoho: 'folder',
  confluence: 'folder',
  quickbooks: 'QuickBooks resource',
  notion: 'page',
  trello: 'Trello list',
} as const;

/**
 * Picker row label when opening the cloud export folder browser (non–Google Picker API flows).
 */
export const PROVIDER_FOLDER_BROWSE_LABELS: Record<(typeof CLOUD_PROVIDER_IDS)[number], string> = {
  'google-drive': 'Browse Google Drive folders...',
  'one-drive': 'Browse OneDrive folders...',
  dropbox: 'Browse Dropbox folders...',
  box: 'Browse Box folders...',
  slack: 'Browse Slack channels...',
  snowflake: 'Browse folders...',
  servicenow: 'Browse folders...',
  'google-cloud-storage': 'Browse folders...',
  hubspot: 'Browse HubSpot folders...',
  signnow: 'Browse SignNow folders...',
  zoho: 'Browse folders...',
  confluence: 'Browse Confluence folders...',
  quickbooks: 'Browse QuickBooks folders...',
  notion: 'Browse Notion pages...',
  trello: 'Browse Trello boards and lists...',
} as const;

/**
 * Providers that require POST method for connection check endpoint
 * These providers use POST instead of GET for /api/v1/nango/connections/check/:provider
 */
export const PROVIDERS_REQUIRING_POST: readonly (typeof CLOUD_PROVIDER_IDS)[number][] = [
  'google-drive',
  'box',
  'slack',
  'signnow',
  'zoho',
  'servicenow',
  'google-cloud-storage',
  'snowflake',
] as const;
